import React, { useEffect, useMemo } from 'react';

import { ProgressBar } from './components/ProgressBar';
import { ResearchTree } from './components/ResearchTree';
import {
  BUILDINGS,
  RESEARCHES,
  ResearchId,
  RECIPES,
  ResearchRequirement,
  RepeatableResearchConfig,
} from './constants';
import {
  PRESTIGE_UPGRADES,
  PrestigeUpgradeId,
  PrestigeUpgradeState,
  isPrestigeUpgradePurchased,
} from './PrestigeSystem';
import {
  ResearchState,
  isResearchUnlocked,
  getNextResearchRequirements,
  getResearchProgress,
  isResearchRepeatable,
  getRepeatableResearchLevel,
  getRepeatableResearchConfig,
  getActiveResearchId,
} from './ResearchSystem';
import './research-menu.css';

interface ResearchMenuResearchProps {
  variant?: 'research';
  isOpen: boolean;
  researchState: ResearchState;
  onSelect: (researchId: ResearchId) => void;
  onClearSelection: () => void;
  onUnlock?: (researchId: ResearchId) => void;
  onForceUnlock?: (researchId: ResearchId) => void;
  debugModeEnabled?: boolean;
  onClose: () => void;
}

interface ResearchMenuPrestigeProps {
  variant: 'prestige';
  isOpen: boolean;
  prestigeState: PrestigeUpgradeState;
  prestigePoints: number;
  onPrestigeSelect: (upgradeId: PrestigeUpgradeId | null) => void;
  onPrestigePurchase: (upgradeId: PrestigeUpgradeId) => void;
  onClose: () => void;
}

type ResearchMenuProps = ResearchMenuResearchProps | ResearchMenuPrestigeProps;

type ResearchListItem = {
  id: ResearchId;
  name: string;
  description: string;
  prerequisites: readonly ResearchId[];
  unlocks: {
    buildings: string[];
    recipes: string[];
  };
  requirements: readonly ResearchRequirement[];
  isRepeatable: boolean;
  repeatableLevel: number;
  repeatableConfig?: RepeatableResearchConfig | null;
  atMaxLevel: boolean;
  status: 'unlocked' | 'available' | 'locked';
  requirementProgress: {
    requirement: ResearchRequirement;
    current: number;
  }[];
  hasRequirements: boolean;
  requirementsMet: boolean;
  totalRequired: number;
  totalCollected: number;
  progressValue: number;
  progressComplete: boolean;
  showProgressBar: boolean;
  treeLevel: number;
  treeOrder: number;
};

type ResearchListItemWithoutLayout = Omit<
  ResearchListItem,
  'treeLevel' | 'treeOrder'
>;

type PrestigeListItem = {
  id: PrestigeUpgradeId;
  name: string;
  description: string;
  effectDescription: string;
  cost: number;
  purchased: boolean;
  affordable: boolean;
};

const formatUnlocks = (researchId: ResearchId): ResearchListItem['unlocks'] => {
  const research = RESEARCHES[researchId];
  const buildingNames = (research.unlocks.buildings ?? []).map(buildingId => {
    const building = Object.values(BUILDINGS).find(
      candidate => candidate.id === buildingId
    );
    return building ? building.name : buildingId;
  });

  const recipeNames = (research.unlocks.recipes ?? []).map(recipeId => {
    const recipe = Object.values(RECIPES).find(
      candidate => candidate.id === recipeId
    );
    return recipe ? recipe.name : recipeId;
  });

  return {
    buildings: buildingNames,
    recipes: recipeNames,
  };
};

const PrestigeResearchMenu: React.FC<ResearchMenuPrestigeProps> = ({
  isOpen,
  prestigeState,
  prestigePoints,
  onPrestigeSelect,
  onPrestigePurchase,
  onClose,
}) => {
  const prestigeItems = useMemo<PrestigeListItem[]>(() => {
    return Object.values(PRESTIGE_UPGRADES).map(upgrade => {
      const purchased = isPrestigeUpgradePurchased(prestigeState, upgrade.id);
      const affordable = prestigePoints >= upgrade.cost;

      return {
        id: upgrade.id,
        name: upgrade.name,
        description: upgrade.description,
        effectDescription: upgrade.effectDescription,
        cost: upgrade.cost,
        purchased,
        affordable,
      };
    });
  }, [prestigeState, prestigePoints]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (prestigeState.selectedUpgrade) {
      return;
    }
    const firstUpgrade = prestigeItems[0];
    if (!firstUpgrade) {
      return;
    }

    onPrestigeSelect(firstUpgrade.id);
  }, [isOpen, prestigeItems, prestigeState.selectedUpgrade, onPrestigeSelect]);

  const selectedUpgradeId =
    prestigeState.selectedUpgrade ?? prestigeItems[0]?.id ?? null;

  const treeNodes = useMemo(
    () =>
      prestigeItems.map(item => {
        const status: 'unlocked' | 'available' | 'locked' = item.purchased
          ? 'unlocked'
          : item.affordable
            ? 'available'
            : 'locked';
        return {
          id: item.id as unknown as ResearchId,
          name: item.name,
          status,
          prerequisites: [] as ResearchId[],
          isRepeatable: false,
          repeatableLevel: item.purchased ? 1 : 0,
          atMaxLevel: item.purchased,
        };
      }),
    [prestigeItems]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className='research-menu-overlay'
      onClick={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className='research-menu research-menu--prestige'
        onClick={event => event.stopPropagation()}
      >
        <div className='research-menu__header research-menu__header--prestige'>
          <h3>✨ Prestige Upgrades</h3>
          <button
            className='research-menu__close'
            onClick={onClose}
            title='Close Prestige Upgrades'
          >
            ✕
          </button>
        </div>

        <div className='research-menu__body research-menu__body--prestige'>
          <div className='prestige-summary'>
            <span>Prestige Points: {prestigePoints}</span>
            {selectedUpgradeId && (
              <span>
                Selected Upgrade:{' '}
                {PRESTIGE_UPGRADES[selectedUpgradeId]?.name ??
                  selectedUpgradeId}
              </span>
            )}
          </div>
          <ResearchTree
            nodes={treeNodes}
            selectedResearchId={
              (selectedUpgradeId as unknown as ResearchId) ?? null
            }
            onSelect={researchId =>
              onPrestigeSelect(researchId as unknown as PrestigeUpgradeId)
            }
            onClearSelection={() => onPrestigeSelect(null)}
            selectedProgress={null}
          />
          {prestigeItems.map(item => {
            const isSelected = item.id === selectedUpgradeId;
            const canPurchase = !item.purchased && item.affordable;
            const statusLabel = item.purchased
              ? 'Purchased'
              : canPurchase
                ? 'Available'
                : 'Locked';

            return (
              <div
                key={item.id}
                className={`research-card research-card--prestige ${
                  isSelected ? 'selected' : ''
                } ${item.purchased ? 'research-card--prestige-owned' : ''}`}
                onClick={() => onPrestigeSelect(item.id)}
              >
                <div className='research-card__header research-card__header--prestige'>
                  <h4>{item.name}</h4>
                  <span className='research-card__status'>{statusLabel}</span>
                </div>
                <p className='research-card__description'>{item.description}</p>
                <p className='research-card__effect'>
                  {item.effectDescription}
                </p>
                <div className='research-card__cost'>
                  Cost: {item.cost} Prestige Point{item.cost === 1 ? '' : 's'}
                </div>
                <div className='research-card__actions'>
                  <button
                    className='research-card__unlock research-card__unlock--prestige'
                    onClick={event => {
                      event.stopPropagation();
                      onPrestigeSelect(item.id);
                      if (canPurchase) {
                        onPrestigePurchase(item.id);
                      }
                    }}
                    disabled={!canPurchase}
                  >
                    {item.purchased ? 'Purchased' : 'Spend Prestige'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const StandardResearchMenu: React.FC<ResearchMenuResearchProps> = ({
  isOpen,
  researchState,
  onSelect,
  onClearSelection,
  onUnlock,
  onForceUnlock,
  debugModeEnabled = false,
  onClose,
}) => {
  const researchItems = useMemo<ResearchListItem[]>(() => {
    const baseItems: ResearchListItemWithoutLayout[] = Object.values(
      RESEARCHES
    ).map(research => {
      const prerequisites = [...research.prerequisites];
      const repeatable = isResearchRepeatable(research.id);
      const repeatableConfig = repeatable
        ? getRepeatableResearchConfig(research.id)
        : null;
      const repeatableLevel = repeatable
        ? getRepeatableResearchLevel(researchState, research.id)
        : 0;
      const unlocked = repeatable
        ? repeatableLevel > 0
        : isResearchUnlocked(researchState, research.id);
      const prerequisitesMet = prerequisites.every(prerequisiteId =>
        isResearchUnlocked(researchState, prerequisiteId)
      );

      const atMaxLevel = Boolean(
        repeatableConfig?.maxLevel !== undefined &&
          repeatableLevel >= repeatableConfig.maxLevel
      );

      let status: ResearchListItem['status'] = 'locked';
      if (repeatable) {
        if (prerequisitesMet && !atMaxLevel) {
          status = 'available';
        } else if (atMaxLevel) {
          status = 'unlocked';
        }
      } else if (unlocked) {
        status = 'unlocked';
      } else if (prerequisitesMet) {
        status = 'available';
      }

      const requirements = getNextResearchRequirements(
        researchState,
        research.id
      );

      const requirementProgress = requirements.map(requirement => {
        const current = getResearchProgress(
          researchState,
          research.id,
          requirement.item
        );

        return {
          requirement,
          current,
        };
      });

      const totalRequired = requirements.reduce(
        (sum, requirement) => sum + requirement.amount,
        0
      );

      const totalCollected = requirementProgress.reduce(
        (sum, { requirement, current }) =>
          sum + Math.min(current, requirement.amount),
        0
      );

      const progressValue =
        totalRequired > 0 ? Math.min(totalCollected / totalRequired, 1) : 0;

      const progressComplete =
        totalRequired > 0 ? totalCollected >= totalRequired : false;

      const showProgressBar = totalRequired > 0;

      const hasRequirements = requirementProgress.length > 0;

      const requirementsMet = requirementProgress.every(progressValue => {
        return progressValue.current >= progressValue.requirement.amount;
      });

      return {
        id: research.id,
        name: research.name,
        description: research.description,
        prerequisites,
        requirements,
        unlocks: formatUnlocks(research.id),
        isRepeatable: repeatable,
        repeatableLevel,
        repeatableConfig,
        atMaxLevel,
        status,
        requirementProgress,
        hasRequirements,
        requirementsMet,
        totalRequired,
        totalCollected,
        progressValue,
        progressComplete,
        showProgressBar,
      };
    });

    const itemsById = new Map<ResearchId, ResearchListItemWithoutLayout>(
      baseItems.map(item => [item.id, item])
    );

    const levelMemo = new Map<ResearchId, number>();
    const resolveLevel = (id: ResearchId): number => {
      const cached = levelMemo.get(id);
      if (cached !== undefined) {
        return cached;
      }

      const item = itemsById.get(id);
      if (!item || item.prerequisites.length === 0) {
        levelMemo.set(id, 0);
        return 0;
      }

      const prerequisiteLevels = item.prerequisites.map(resolveLevel);
      const level = Math.max(...prerequisiteLevels) + 1;
      levelMemo.set(id, level);
      return level;
    };

    const levelBuckets = new Map<number, ResearchListItemWithoutLayout[]>();
    baseItems.forEach(item => {
      const level = resolveLevel(item.id);
      const bucket = levelBuckets.get(level);
      if (bucket) {
        bucket.push(item);
      } else {
        levelBuckets.set(level, [item]);
      }
    });

    const orderById = new Map<ResearchId, number>();
    Array.from(levelBuckets.entries())
      .sort((a, b) => a[0] - b[0])
      .forEach(([, bucket]) => {
        const sortedBucket = [...bucket].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        sortedBucket.forEach((item, index) => {
          orderById.set(item.id, index);
        });
      });

    const statusPriority: Record<ResearchListItem['status'], number> = {
      available: 0,
      locked: 1,
      unlocked: 2,
    };

    return baseItems
      .map(item => {
        const treeLevel = resolveLevel(item.id);
        const treeOrder = orderById.get(item.id) ?? 0;
        return {
          ...item,
          treeLevel,
          treeOrder,
        };
      })
      .sort((a, b) => {
        const statusDifference =
          statusPriority[a.status] - statusPriority[b.status];
        if (statusDifference !== 0) {
          return statusDifference;
        }

        const levelDifference = a.treeLevel - b.treeLevel;
        if (levelDifference !== 0) {
          return levelDifference;
        }

        const orderDifference = a.treeOrder - b.treeOrder;
        if (orderDifference !== 0) {
          return orderDifference;
        }

        return a.name.localeCompare(b.name);
      });
  }, [researchState]);

  const queuedResearchIds = researchState.queue;
  const selectedResearchId = getActiveResearchId(researchState);

  const selectedResearchItem = useMemo(() => {
    if (!selectedResearchId) {
      return null;
    }

    return (
      researchItems.find(research => research.id === selectedResearchId) ?? null
    );
  }, [researchItems, selectedResearchId]);

  const selectedProgress = useMemo(() => {
    if (!selectedResearchItem) {
      return null;
    }

    return {
      progressValue: selectedResearchItem.progressValue,
      progressComplete: selectedResearchItem.progressComplete,
      showProgressBar: selectedResearchItem.showProgressBar,
      completedText: selectedResearchItem.isRepeatable
        ? 'Ready to upgrade'
        : 'Ready to unlock',
      label: selectedResearchItem.isRepeatable
        ? 'Upgrade Progress'
        : 'Research Progress',
      requirements: selectedResearchItem.requirementProgress,
      totalCollected: selectedResearchItem.totalCollected,
      totalRequired: selectedResearchItem.totalRequired,
      hasRequirements: selectedResearchItem.hasRequirements,
      isRepeatable: selectedResearchItem.isRepeatable,
      repeatableLevel: selectedResearchItem.repeatableLevel,
      atMaxLevel: selectedResearchItem.atMaxLevel,
      status: selectedResearchItem.status,
    } as const;
  }, [selectedResearchItem]);

  const treeNodes = useMemo(
    () =>
      researchItems.map(item => ({
        id: item.id,
        name: item.name,
        status: item.status,
        prerequisites: item.prerequisites,
        isRepeatable: item.isRepeatable,
        repeatableLevel: item.repeatableLevel,
        atMaxLevel: item.atMaxLevel,
      })),
    [researchItems]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className='research-menu-overlay'
      onClick={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className='research-menu' onClick={event => event.stopPropagation()}>
        <div className='research-menu__header'>
          <h3>🔬 Research</h3>
          <button
            className='research-menu__close'
            onClick={onClose}
            title='Close Research Menu'
          >
            ✕
          </button>
        </div>

        <div className='research-menu__body'>
          <div className='research-menu__queue'>
            <div className='research-menu__queue-header'>
              <h4>Research Queue</h4>
              <button
                type='button'
                className='research-menu__queue-clear'
                onClick={onClearSelection}
                disabled={queuedResearchIds.length === 0}
              >
                Clear Queue
              </button>
            </div>
            {queuedResearchIds.length === 0 ? (
              <p className='research-menu__queue-empty'>
                Select a research to queue any required prerequisites.
              </p>
            ) : (
              <ol className='research-menu__queue-list'>
                {queuedResearchIds.map((queuedResearchId, index) => {
                  const name =
                    RESEARCHES[queuedResearchId]?.name ?? queuedResearchId;
                  const isActive = index === 0;
                  const queueLabel = isActive
                    ? 'Active'
                    : `Queued #${index + 1}`;
                  return (
                    <li
                      key={queuedResearchId}
                      className={
                        isActive
                          ? 'research-menu__queue-item research-menu__queue-item--active'
                          : 'research-menu__queue-item'
                      }
                    >
                      <span className='research-menu__queue-rank'>
                        {queueLabel}
                      </span>
                      <span className='research-menu__queue-name'>{name}</span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
          <ResearchTree
            nodes={treeNodes}
            selectedResearchId={selectedResearchId}
            onSelect={onSelect}
            onClearSelection={onClearSelection}
            selectedProgress={selectedProgress}
            debugModeEnabled={debugModeEnabled}
            {...(onForceUnlock ? { onForceUnlock } : {})}
          />
          {researchItems.map(item => {
            const unmetPrerequisites = item.prerequisites.filter(
              prerequisiteId =>
                !isResearchUnlocked(researchState, prerequisiteId)
            );

            const isRepeatable = item.isRepeatable;
            const isUnlocked = isRepeatable
              ? item.repeatableLevel > 0
              : item.status === 'unlocked';
            const isAvailable = item.status === 'available';
            const atMaxLevel = item.atMaxLevel;
            const queueIndex = queuedResearchIds.indexOf(item.id);
            const isQueued = queueIndex >= 0;
            const isSelected = queueIndex === 0;

            const requirementProgress = item.requirementProgress;
            const hasRequirements = item.hasRequirements;
            const requirementsMet = item.requirementsMet;
            const progressValue = item.progressValue;
            const progressComplete = item.progressComplete;
            const showProgressBar = item.showProgressBar;

            const canUnlock = isRepeatable
              ? !atMaxLevel && (hasRequirements ? requirementsMet : isAvailable)
              : !isUnlocked &&
                (hasRequirements ? requirementsMet : isAvailable);

            const repeatableTypeLabel = (() => {
              if (!item.repeatableConfig) {
                return 'speed';
              }
              switch (item.repeatableConfig.type) {
                case 'belt-speed':
                  return 'belt speed';
                case 'assembler-speed':
                  return 'assembler speed';
                case 'factory-speed':
                  return 'factory speed';
                case 'miner-speed':
                  return 'miner speed';
                case 'output-capacity':
                  return 'output capacity';
                case 'belt-stacking':
                  return 'belt stacking';
                case 'generator-output':
                  return 'generator output';
                default:
                  return 'speed';
              }
            })();

            const statusLabel = (() => {
              if (isRepeatable) {
                if (atMaxLevel) {
                  return `Level ${item.repeatableLevel} (Max)`;
                }
                if (isSelected) {
                  return `Level ${item.repeatableLevel} • Active`;
                }
                if (isQueued) {
                  return `Level ${item.repeatableLevel} • Queued #${
                    queueIndex + 1
                  }`;
                }
                if (item.repeatableLevel > 0) {
                  return `Level ${item.repeatableLevel}`;
                }
                return isAvailable ? 'Available' : 'Locked';
              }

              if (isUnlocked) {
                return 'Unlocked';
              }
              if (isSelected) {
                return 'Active';
              }
              if (isQueued) {
                return `Queued #${queueIndex + 1}`;
              }
              if (isAvailable) {
                return 'Available';
              }
              return 'Locked';
            })();

            const repeatableInfo = (() => {
              if (!item.repeatableConfig) {
                return null;
              }

              if (item.repeatableConfig.type === 'output-capacity') {
                return 'Doubles output buffer per level';
              }

              if (item.repeatableConfig.type === 'belt-stacking') {
                return 'Doubles belt item stack per level';
              }

              return `+${Math.round(item.repeatableConfig.bonusPerLevel * 100)}% ${repeatableTypeLabel} per level`;
            })();

            const unlockButtonLabel = (() => {
              if (isRepeatable) {
                if (atMaxLevel) {
                  return 'Max Level';
                }
                if (item.repeatableLevel > 0) {
                  return `Upgrade to L${item.repeatableLevel + 1}`;
                }
                return 'Unlock';
              }
              return isUnlocked ? 'Unlocked' : 'Unlock';
            })();

            const unlockButtonDisabled = (() => {
              if (isRepeatable) {
                return atMaxLevel || !canUnlock || !onUnlock;
              }
              if (isUnlocked) {
                return true;
              }
              return !canUnlock || !onUnlock;
            })();

            const showSetActiveButton = isRepeatable
              ? !atMaxLevel
              : !isUnlocked;

            const setActiveDisabled =
              atMaxLevel || isSelected || isQueued || !onSelect;
            const setActiveLabel = (() => {
              if (isSelected) {
                return 'Active';
              }
              if (isQueued) {
                return `Queued #${queueIndex + 1}`;
              }
              return 'Set Active';
            })();

            return (
              <div
                key={item.id}
                className={`research-card research-card--${item.status}`}
              >
                <div className='research-card__header'>
                  <div className='research-card__header-content'>
                    <h4>{item.name}</h4>
                    <p className='research-card__description'>
                      {item.description}
                    </p>
                    {repeatableInfo && (
                      <p className='research-card__repeatable-info'>
                        {repeatableInfo}
                      </p>
                    )}
                  </div>
                  <div className='research-card__status'>{statusLabel}</div>
                </div>

                <div className='research-card__content'>
                  {item.prerequisites.length > 0 && (
                    <div className='research-card__prerequisites'>
                      <span>Prerequisites:</span>
                      <ul>
                        {item.prerequisites.map(prerequisiteId => {
                          const prerequisite = RESEARCHES[prerequisiteId];
                          const met =
                            !unmetPrerequisites.includes(prerequisiteId);
                          return (
                            <li
                              key={prerequisiteId}
                              className={met ? 'met' : 'unmet'}
                            >
                              {prerequisite.name}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {(item.unlocks.buildings.length > 0 ||
                    item.unlocks.recipes.length > 0) && (
                    <div className='research-card__unlocks'>
                      <span>Unlocks:</span>
                      <ul>
                        {item.unlocks.buildings.map(name => (
                          <li key={`building-${name}`}>🏗️ {name}</li>
                        ))}
                        {item.unlocks.recipes.map(name => (
                          <li key={`recipe-${name}`}>⚙️ {name}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {item.requirements.length > 0 && (
                    <div className='research-card__requirements'>
                      <span>Requirements:</span>
                      <ul>
                        {requirementProgress.map(({ requirement, current }) => (
                          <li
                            key={`${item.id}-${requirement.item}`}
                            className={
                              current >= requirement.amount ? 'met' : 'unmet'
                            }
                          >
                            {`${requirement.amount}x ${requirement.item} (${current}/${requirement.amount})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {showProgressBar && (
                  <div className='research-card__progress'>
                    <ProgressBar
                      progress={progressValue}
                      label={
                        item.isRepeatable ? 'Upgrade Progress:' : 'Progress:'
                      }
                      isComplete={progressComplete}
                      completedText={
                        item.isRepeatable
                          ? 'Ready to upgrade'
                          : 'Ready to unlock'
                      }
                    />
                  </div>
                )}

                <div className='research-card__actions'>
                  {showSetActiveButton && (
                    <button
                      type='button'
                      className='research-card__set-active'
                      disabled={setActiveDisabled}
                      onClick={() => {
                        if (isSelected) {
                          onClearSelection();
                          return;
                        }
                        if (setActiveDisabled) {
                          return;
                        }
                        onSelect(item.id);
                      }}
                    >
                      {setActiveLabel}
                    </button>
                  )}

                  <button
                    type='button'
                    className='research-card__unlock'
                    disabled={unlockButtonDisabled}
                    onClick={() => {
                      if (!onUnlock) {
                        return;
                      }
                      if (isRepeatable) {
                        if (atMaxLevel || !canUnlock) {
                          return;
                        }
                        onUnlock(item.id);
                        return;
                      }
                      if (isUnlocked || !canUnlock) {
                        return;
                      }
                      onUnlock(item.id);
                    }}
                  >
                    {unlockButtonLabel}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const ResearchMenu: React.FC<ResearchMenuProps> = props => {
  if (props.variant === 'prestige') {
    return <PrestigeResearchMenu {...props} />;
  }

  return <StandardResearchMenu {...props} />;
};

export default ResearchMenu;
